import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import cardApi from '~/pages/CardSaga/PowerCard/powerCard.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchPowerCardApi = createAsyncThunk(
    'cardSaga/powerCard/fetchPowerCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await cardApi.getAll();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const togglePowerCardPublishedStateApi = createAsyncThunk(
    'cardSaga/powerCard/togglePowerCardPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);
            throw rejectWithValue([]); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const createPowerCardApi = createAsyncThunk(
    'cardSaga/powerCard/createPowerCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await cardApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storePowerCardApi = createAsyncThunk(
    'cardSaga/powerCard/storePowerCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            await cardApi.store(values);

            alertSwal('success', 'Card generated successfully!');
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValuePower`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-powers
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const showPowerCardApi = createAsyncThunk(
    'cardSaga/powerCard/showPowerCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await cardApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updatePowerCardApi = createAsyncThunk(
    'cardSaga/powerCard/updatePowerCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            await cardApi.update(id, values);
            alertSwal('success', 'Card updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValuePower`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-powers
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const deletePowerCardItemApi = createAsyncThunk(
    'cardSaga/powerCard/deletePowerCardItemApi',
    async (params, { dispatch }) => {
        const { navigate, id } = params;

        try {
            await cardApi.delete(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const powerCardSlice = createSlice({
    name: 'cardSaga/powerCard',
    initialState: { rows: [], emptyModel: {}, editedModel: {}, frontSides: [], backSides: [], allPictures: [] },
    reducers: {
        clearEditedPowerCard(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPowerCardApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchPowerCardApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(togglePowerCardPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(togglePowerCardPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createPowerCardApi.fulfilled, (state, { payload }) => {
                state.emptyModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.allPictures = payload.allPictures;
            })
            .addCase(createPowerCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(storePowerCardApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storePowerCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showPowerCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.allPictures = payload.allPictures;
            })
            .addCase(showPowerCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(updatePowerCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make PowerUpdate re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
            })
            .addCase(updatePowerCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deletePowerCardItemApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedPowerCard } = powerCardSlice.actions;
